/**
 * IndexedDB schema.
 *
 * This is the local cache and the thing the app boots from — it renders before
 * any network call. OneDrive is the source of truth for sync across devices,
 * not for startup.
 */

import Dexie, { type Table } from 'dexie';
import type { Budget, Card, Category, Person, Rule, Settings, Statement, Tombstone, Transaction } from '../domain/types.ts';

export interface SettingsRow {
  key: 'settings';
  value: Settings;
}

/** What we remember about the last successful sync with OneDrive. */
export interface SyncMeta {
  /** Increased atomically with local writes; uploads acknowledge one revision. */
  revision: number;
  /** Graph eTags, so a write can be conditional and detect a remote change. */
  configETag: string | null;
  dataETag: string | null;
  /** ISO timestamp of the last completed sync. */
  lastSyncedAt: string | null;
  /** Signed-in account identifier, so switching accounts resets the eTags. */
  accountId: string | null;
  /**
   * Local changes exist that have not reached OneDrive. Set by every write,
   * cleared by a successful sync — so closing the laptop mid-import leaves a
   * visible "pending" state instead of quietly diverging.
   */
  dirty: boolean;
}

export const DEFAULT_SYNC_META: SyncMeta = {
  revision: 0,
  configETag: null,
  dataETag: null,
  lastSyncedAt: null,
  accountId: null,
  dirty: false,
};

export interface SyncMetaRow {
  key: 'sync';
  value: SyncMeta;
}

export class ExpensesDatabase extends Dexie {
  transactions!: Table<Transaction, string>;
  statements!: Table<Statement, string>;
  people!: Table<Person, string>;
  cards!: Table<Card, string>;
  categories!: Table<Category, string>;
  rules!: Table<Rule, string>;
  budgets!: Table<Budget, string>;
  settings!: Table<SettingsRow, string>;
  syncMeta!: Table<SyncMetaRow, string>;
  tombstones!: Table<Tombstone, [string, string]>;

  constructor(name = 'expenses') {
    super(name);

    this.version(1).stores({
      // Compound indexes cover the filter bar's hot paths: person+date and
      // card+date range scans.
      transactions:
        'id, dedupeKey, sourceFileId, categoryId, merchantNormalized, transactionDate, ' +
        'chargeDate, [personId+transactionDate], [cardId+transactionDate]',
      statements: 'id, fileHash, cardId, personId, importedAt',
      people: 'id',
      cards: 'id, personId, [issuer+last4]',
      categories: 'id, parentId',
      rules: 'id, order',
      budgets: 'id, categoryId',
      settings: 'key',
    });

    // v2 adds the sync-metadata store and Transaction.updatedAt. Rows written
    // before updatedAt existed have not been touched since import, so their
    // import time is the correct stamp — same rule as the JSON migration in
    // domain/schema.ts, applied to the local cache.
    this.version(2)
      .stores({ syncMeta: 'key' })
      .upgrade(async (tx) => {
        await tx
          .table<Transaction>('transactions')
          .toCollection()
          .modify((row) => {
            if (row.updatedAt === undefined) row.updatedAt = row.importedAt;
          });
      });

    /*
     * v3 records deletions so they survive a sync merge. The primary key is
     * compound — an id is only unique within its collection, and one global id
     * space would let a stray collision delete the wrong row.
     *
     * No upgrade function: an empty table is correct. Deletions made before
     * this version were already lost to the union and cannot be recovered.
     */
    this.version(3).stores({ tombstones: '[collection+id], collection, deletedAt' });

    /*
     * v4 adds standalone `cardId` and `personId` indexes on transactions.
     *
     * A compound index is not usable as a prefix in Dexie: with only
     * `[cardId+transactionDate]`, `where('cardId')` throws SchemaError rather
     * than scanning. Every "all rows of this card" and "all rows of this
     * person" query — reassigning a card, deleting a person, repointing
     * attribution — needs the plain index, and those calls were failing.
     *
     * No upgrade function: adding an index rebuilds it from the existing rows.
     */
    this.version(4).stores({
      transactions:
        'id, dedupeKey, sourceFileId, categoryId, merchantNormalized, transactionDate, ' +
        'chargeDate, cardId, personId, [personId+transactionDate], [cardId+transactionDate]',
    });

    // v5 classifies each card/account as Israel or abroad. Existing sources
    // have no evidence of being abroad, so retain their historical behaviour
    // by defaulting them to Israel; the user can change any source in Settings.
    this.version(5).upgrade(async (tx) => {
      await tx
        .table<Card>('cards')
        .toCollection()
        .modify((row) => {
          if (row.region === undefined) row.region = 'israel';
        });
    });

    // Unknown legacy modification times must not outrank later edits.
    this.version(6).upgrade(async (tx) => {
      for (const name of ['people', 'cards', 'categories', 'rules', 'budgets']) {
        await tx.table(name).toCollection().modify((row) => {
          row.updatedAt ??= '1970-01-01T00:00:00.000Z';
        });
      }
      await tx.table('settings').toCollection().modify((row) => {
        row.value.updatedAt ??= '1970-01-01T00:00:00.000Z';
      });
    });
  }
}

export const db = new ExpensesDatabase();
